// ============================================================================
// TournamentSidebar - slide-in drawer with Ranking / Prize / Tables / Blinds
// ============================================================================
// Mirrors the CoinPoker tournament side panel: live chip stacks across all
// tables, prize structure, table list, current blind level + countdown. Each
// player row in Ranking links to spectate that player's table.
// ============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerBody, DrawerHeader,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Box, Text, HStack, VStack, Input, Badge, Spinner, Button, Divider,
} from '@chakra-ui/react';
import { Trophy, Eye, Users as UsersIcon, Clock, DollarSign, Search } from 'lucide-react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

interface TournamentSidebarProps {
  tournamentId: string;
  currentUserId: string;
  isOpen: boolean;
  onClose: () => void;
  // Optional uid → display name map so the leaderboard doesn't fall back to
  // raw UID-looking strings ("kITcBDdf") when player.name is missing from
  // gameState. GamePage already owns this map for the table; passing it
  // through avoids a separate /users fan-out.
  usernames?: { [uid: string]: string };
}

interface SeatedPlayer {
  uid: string;
  username: string;
  chips: number;
  tableId: string;
  isBot: boolean;
}

const TournamentSidebar: React.FC<TournamentSidebarProps> = ({
  tournamentId, currentUserId, isOpen, onClose, usernames = {},
}) => {
  const navigate = useNavigate();
  const [tournament, setTournament] = useState<any | null>(null);
  const [tables, setTables] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [nowTick, setNowTick] = useState<number>(Date.now());

  // Subscribe to tournament doc
  useEffect(() => {
    if (!isOpen || !tournamentId) return;
    const unsub = onSnapshot(doc(db, 'tournaments', tournamentId), snap => {
      if (snap.exists()) setTournament({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
    return () => unsub();
  }, [isOpen, tournamentId]);

  // Subscribe to all tables in this tournament
  useEffect(() => {
    if (!isOpen || !tournamentId) return;
    const q = query(collection(db, 'tables'), where('tournamentId', '==', tournamentId));
    const unsub = onSnapshot(q, snap => {
      setTables(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [isOpen, tournamentId]);

  // Tick every second for level countdown
  useEffect(() => {
    if (!isOpen) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isOpen]);

  // Readable fallback when a seat row has no name: bots get "Bot XXXX"
  // instead of the raw truncated uid ("house_bo").
  const fallbackName = (uid: string): string =>
    uid.startsWith('house_bot_') ? `Bot ${uid.slice(-4)}` : uid.substring(0, 8);

  // Flatten all seated players across all tournament tables, sort by chips desc
  const seatedPlayers: SeatedPlayer[] = useMemo(() => {
    // Authoritative bust list from the tournament doc: a uid in here is OUT
    // even if a stale seat row still lingers in some table's gameState.
    const eliminatedSet = new Set<string>(tournament?.eliminatedPlayers || []);
    const tableMap: Record<string, string> = tournament?.playerTableMap || {};
    const byUid = new Map<string, SeatedPlayer>();
    for (const t of tables) {
      const gsPlayers: any[] = t.gameState?.players || [];
      for (const p of gsPlayers) {
        if (!p?.id) continue;
        // Eliminated seat rows linger in gameState until the next hand
        // rebuilds the table. Counting them made busted players show as
        // "seated with 0 chips" and inflated the entrant count.
        if (p.eliminated || eliminatedSet.has(p.id)) continue;
        // chips + currentBet = committed stack, so an all-in player keeps
        // their real rank instead of dropping to 0 mid-hand and bouncing
        // back after the pot resolves.
        const committed = (p.chips || 0) + (p.currentBet || 0);
        // Zero-stack rows that aren't all-in are ghost seats (left behind
        // by a move/rebuy) or busts pending recording - never rank them.
        if (committed <= 0 && !p.allIn) continue;
        // Name priority: live gameState name → caller-supplied uid map →
        // readable fallback. The uid map covers the case where gameState
        // was written before the player's username was resolved.
        const resolved = p.name || usernames[p.id] || fallbackName(p.id);
        const row: SeatedPlayer = {
          uid: p.id,
          username: resolved,
          chips: committed,
          tableId: t.id,
          isBot: typeof p.id === 'string' && p.id.startsWith('house_bot_'),
        };
        const existing = byUid.get(p.id);
        if (!existing) { byUid.set(p.id, row); continue; }
        // Duplicate seat rows across tables (stale ghost after a move or
        // rebuy): trust the tournament's playerTableMap for which table is
        // real; without a mapping keep the larger stack.
        const mapped = tableMap[p.id];
        if (mapped) {
          if (mapped === t.id) byUid.set(p.id, row);
        } else if (row.chips > existing.chips) {
          byUid.set(p.id, row);
        }
      }
    }
    const out = [...byUid.values()];
    out.sort((a, b) => b.chips - a.chips);
    return out;
  }, [tables, usernames, tournament]);

  const filteredPlayers = useMemo(() => {
    if (!search.trim()) return seatedPlayers;
    const s = search.toLowerCase();
    return seatedPlayers.filter(p => p.username.toLowerCase().includes(s));
  }, [seatedPlayers, search]);

  const myRank = useMemo(() => {
    const idx = seatedPlayers.findIndex(p => p.uid === currentUserId);
    return idx >= 0 ? idx + 1 : null;
  }, [seatedPlayers, currentUserId]);

  // Look up a display name for an arbitrary UID. Falls back to a readable
  // name if the user isn't a seated player (e.g. already eliminated).
  const getPlayerDisplayName = (uid: string, players: SeatedPlayer[]): string => {
    const found = players.find(p => p.uid === uid);
    return found?.username || usernames[uid] || fallbackName(uid);
  };

  // Field numbers, CoinPoker-style: Entrants = everyone who entered (the
  // immutable field), Left = still alive. seatedPlayers alone undercounts
  // during late reg (registered-but-unseated) and used to OVERCOUNT when
  // ghost seat rows lingered - use the tournament doc as the authority.
  const fieldSize = (tournament?.registeredPlayers?.length as number)
    || tournament?.currentPlayers || seatedPlayers.length;
  const leftCount = tournament?.registeredPlayers?.length
    ? Math.max(seatedPlayers.length,
        fieldSize - (tournament?.eliminatedPlayers?.length || 0))
    : seatedPlayers.length;

  // Current blind level info. Reads both blindLevelStartedAt (raw ms,
  // written by startTournament and the deal-next-hand auto-advance) and
  // currentLevelStartedAt (Timestamp, written by adminAdvanceBlindLevel) so
  // a manual admin advance doesn't blank out the countdown.
  const blindInfo = useMemo(() => {
    if (!tournament) return null;
    const levels: any[] = tournament.blindLevels || [];
    const currentLevel = tournament.currentBlindLevel || 1;
    const current = levels.find(l => l.level === currentLevel) || levels[0];
    const next = levels.find(l => l.level === currentLevel + 1);
    let levelStartMs = 0;
    if (typeof tournament.blindLevelStartedAt === 'number' && tournament.blindLevelStartedAt > 0) {
      levelStartMs = tournament.blindLevelStartedAt;
    } else if (typeof tournament.currentLevelStartedAt?.toMillis === 'function') {
      levelStartMs = tournament.currentLevelStartedAt.toMillis();
    } else if (typeof tournament.startTime?.toMillis === 'function') {
      levelStartMs = tournament.startTime.toMillis();
    }
    const durationMs = ((current?.duration || tournament.blindLevelDuration || 10)) * 60 * 1000;
    const elapsed = nowTick - levelStartMs;
    const remaining = Math.max(0, durationMs - elapsed);
    const mm = Math.floor(remaining / 60000);
    const ss = Math.floor((remaining % 60000) / 1000);
    return {
      current, next, levels,
      currentLevel,
      countdown: levelStartMs > 0
        ? `${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`
        : '--:--',
    };
  }, [tournament, nowTick]);

  // Payout structure with computed amounts
  const payoutRows = useMemo(() => {
    if (!tournament) return [];
    const structure: any[] = tournament.payoutStructure || [];
    const pool = tournament.prizePool || 0;
    return structure.map(s => ({
      position: s.position,
      percent: s.percentage,
      amount: Math.round((pool * s.percentage / 100) * 100) / 100,
    }));
  }, [tournament]);

  const spectatePlayer = (tableId: string) => {
    onClose();
    navigate(`/game/${tableId}?spectate=1&tournamentId=${tournamentId}`);
  };

  const currency = tournament?.currency || 'pkcash';
  const moneyPrefix = currency === 'playcoins' ? '' : '$';
  const moneySuffix = currency === 'playcoins' ? ' PC' : '';
  const fmt = (n: number) => `${moneyPrefix}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}${moneySuffix}`;

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent bg="gray.900" color="white">
        <DrawerCloseButton color="gray.400" />
        <DrawerHeader borderBottomWidth="1px" borderColor="gray.700" pb={3}>
          <HStack>
            <Trophy size={18} color="#fbbf24" />
            <Text fontSize="md" fontWeight="bold">{tournament?.name || 'Tournament'}</Text>
          </HStack>
          {tournament && (
            <HStack mt={2} spacing={4} fontSize="xs" color="gray.400">
              <Box><Text as="span" color="gray.500">Entrants </Text>{fieldSize}</Box>
              <Box><Text as="span" color="gray.500">Left </Text>{leftCount}</Box>
              <Box><Text as="span" color="gray.500">Prize </Text>{fmt(tournament.prizePool || 0)}</Box>
              {myRank && <Box><Text as="span" color="gray.500">Rank </Text>
                <Text as="span" color="cyan.300" fontWeight="bold">{myRank}/{leftCount}</Text>
              </Box>}
            </HStack>
          )}
        </DrawerHeader>

        <DrawerBody p={0}>
          {loading ? (
            <Box p={6} textAlign="center"><Spinner color="cyan.400" /></Box>
          ) : (
            <Tabs colorScheme="cyan" variant="line" isFitted>
              <TabList borderColor="gray.700">
                <Tab color="gray.400" _selected={{ color: 'cyan.300' }}>Ranking</Tab>
                <Tab color="gray.400" _selected={{ color: 'cyan.300' }}>Prize</Tab>
                <Tab color="gray.400" _selected={{ color: 'cyan.300' }}>Tables</Tab>
                <Tab color="gray.400" _selected={{ color: 'cyan.300' }}>Blinds</Tab>
              </TabList>

              <TabPanels>
                {/* ── Ranking ────────────────────────────────────────────── */}
                <TabPanel px={3} py={3}>
                  <HStack mb={3}>
                    <Search size={14} color="#9ca3af" />
                    <Input value={search} onChange={e => setSearch(e.target.value)}
                      placeholder="Search player" size="sm" bg="gray.800"
                      borderColor="gray.700" />
                  </HStack>
                  <VStack align="stretch" spacing={1}>
                    {filteredPlayers.length === 0 && (
                      <Text color="gray.500" fontSize="sm" textAlign="center" py={6}>
                        {seatedPlayers.length === 0 ? 'No players seated yet' : 'No matches'}
                      </Text>
                    )}
                    {filteredPlayers.map((p, idx) => {
                      const rank = seatedPlayers.findIndex(sp => sp.uid === p.uid) + 1;
                      const isMe = p.uid === currentUserId;
                      return (
                        <HStack key={p.uid} px={2} py={2}
                          bg={isMe ? 'cyan.900' : idx % 2 === 0 ? 'gray.800' : 'transparent'}
                          borderRadius="sm" spacing={3}>
                          <Text fontSize="xs" color="gray.500" w="28px">{rank}</Text>
                          <Box flex={1} minW={0}>
                            <Text fontSize="sm" color={isMe ? 'cyan.200' : 'white'}
                              fontWeight={isMe ? 'bold' : 'normal'} isTruncated>
                              {p.username}
                              {p.isBot && <Badge ml={2} colorScheme="purple" fontSize="9px">BOT</Badge>}
                            </Text>
                          </Box>
                          <Text fontSize="sm" color="gray.300" w="80px" textAlign="right">
                            {p.chips.toLocaleString()}
                          </Text>
                          <Button size="xs" variant="ghost" colorScheme="cyan"
                            leftIcon={<Eye size={12} />}
                            onClick={() => spectatePlayer(p.tableId)}>
                            Watch
                          </Button>
                        </HStack>
                      );
                    })}
                  </VStack>

                  {/* ── ELIMINATIONS (busts so far) ─────────────────────── */}
                  {Array.isArray(tournament?.eliminations) && tournament.eliminations.length > 0 && (
                    <Box mt={4}>
                      <Text fontSize="xs" color="gray.500" letterSpacing="wide" fontWeight="bold" mb={2}>
                        ELIMINATED
                      </Text>
                      <VStack align="stretch" spacing={1}>
                        {[...tournament.eliminations]
                          .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
                          .map((e: any, i: number) => (
                            <HStack key={`${e.playerId}-${i}`} px={2} py={1.5}
                              bg={i % 2 === 0 ? 'gray.800' : 'transparent'}
                              borderRadius="sm" spacing={3}>
                              <Text fontSize="xs" color="gray.500" w="36px">
                                #{e.position}
                              </Text>
                              <Box flex={1} minW={0}>
                                <Text fontSize="sm" color="gray.300" isTruncated>
                                  {e.playerName || (e.playerId ? fallbackName(e.playerId) : 'Unknown')}
                                </Text>
                                {e.eliminatedBy && (
                                  <Text fontSize="2xs" color="gray.600" isTruncated>
                                    KO'd by {getPlayerDisplayName(e.eliminatedBy, seatedPlayers)}
                                  </Text>
                                )}
                              </Box>
                            </HStack>
                          ))}
                      </VStack>
                    </Box>
                  )}
                </TabPanel>

                {/* ── Prize ──────────────────────────────────────────────── */}
                <TabPanel px={3} py={3}>
                  <VStack align="stretch" spacing={3} mb={4}>
                    <HStack justify="space-between" bg="gray.800" p={3} borderRadius="md">
                      <Text fontSize="sm" color="gray.400">Total prize pool</Text>
                      <Text fontSize="lg" fontWeight="bold" color="yellow.300">
                        {fmt(tournament?.prizePool || 0)}
                      </Text>
                    </HStack>
                  </VStack>
                  <VStack align="stretch" spacing={1}>
                    {payoutRows.length === 0 && (
                      <Text color="gray.500" fontSize="sm" textAlign="center" py={6}>
                        Payout structure not set
                      </Text>
                    )}
                    {payoutRows.map(r => (
                      <HStack key={r.position} px={3} py={2}
                        bg={r.position % 2 === 1 ? 'gray.800' : 'transparent'}
                        borderRadius="sm">
                        <Text w="48px" fontSize="sm" color="gray.400">
                          #{r.position}
                        </Text>
                        <Text flex={1} fontSize="sm" color="gray.500">
                          {r.percent.toFixed(1)}%
                        </Text>
                        <Text fontSize="sm" color="yellow.300" fontWeight="bold">
                          {fmt(r.amount)}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </TabPanel>

                {/* ── Tables ─────────────────────────────────────────────── */}
                <TabPanel px={3} py={3}>
                  <VStack align="stretch" spacing={1}>
                    {tables.length === 0 && (
                      <Text color="gray.500" fontSize="sm" textAlign="center" py={6}>
                        No active tables
                      </Text>
                    )}
                    {tables.map(t => {
                      const playerCount = (t.gameState?.players || []).filter((p: any) => p).length;
                      return (
                        <HStack key={t.id} px={3} py={2} bg="gray.800" borderRadius="sm">
                          <UsersIcon size={14} color="#9ca3af" />
                          <Box flex={1} minW={0}>
                            <Text fontSize="sm" isTruncated>{t.name || t.id}</Text>
                            <Text fontSize="xs" color="gray.500">{playerCount} seated</Text>
                          </Box>
                          <Button size="xs" variant="ghost" colorScheme="cyan"
                            leftIcon={<Eye size={12} />}
                            onClick={() => spectatePlayer(t.id)}>
                            Watch
                          </Button>
                        </HStack>
                      );
                    })}
                  </VStack>
                </TabPanel>

                {/* ── Blinds ─────────────────────────────────────────────── */}
                <TabPanel px={3} py={3}>
                  {blindInfo && (
                    <VStack align="stretch" spacing={4}>
                      <Box bg="gray.800" p={4} borderRadius="md">
                        <HStack justify="space-between" mb={2}>
                          <Text fontSize="sm" color="gray.400">Level {blindInfo.currentLevel}</Text>
                          <HStack spacing={1}>
                            <Clock size={12} color="#9ca3af" />
                            <Text fontSize="sm" fontFamily="mono" color="cyan.300">
                              {blindInfo.countdown}
                            </Text>
                          </HStack>
                        </HStack>
                        {blindInfo.current && (
                          <Text fontSize="2xl" fontWeight="bold" color="white">
                            {blindInfo.current.smallBlind?.toLocaleString()}/
                            {blindInfo.current.bigBlind?.toLocaleString()}
                            {blindInfo.current.ante > 0 &&
                              <Text as="span" fontSize="md" color="gray.400" ml={2}>
                                ante {blindInfo.current.ante.toLocaleString()}
                              </Text>}
                          </Text>
                        )}
                      </Box>

                      {blindInfo.next && (
                        <Box bg="gray.800" p={3} borderRadius="md" opacity={0.7}>
                          <Text fontSize="xs" color="gray.500">Next level</Text>
                          <Text fontSize="md" color="gray.300">
                            {blindInfo.next.smallBlind?.toLocaleString()}/
                            {blindInfo.next.bigBlind?.toLocaleString()}
                          </Text>
                        </Box>
                      )}

                      <Divider borderColor="gray.700" />

                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={2}>All levels</Text>
                        <VStack align="stretch" spacing={1} maxH="300px" overflowY="auto">
                          {blindInfo.levels.map((l: any) => (
                            <HStack key={l.level} px={2} py={1}
                              bg={l.level === blindInfo.currentLevel ? 'cyan.900' : 'transparent'}
                              borderRadius="sm">
                              <Text fontSize="xs" color="gray.500" w="30px">L{l.level}</Text>
                              <Text fontSize="xs" flex={1}>
                                {l.smallBlind?.toLocaleString()}/{l.bigBlind?.toLocaleString()}
                                {l.ante > 0 && <Text as="span" color="gray.500"> · {l.ante}</Text>}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    </VStack>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default TournamentSidebar;
